import React, { type ReactElement, type JSX, cloneElement } from "react";
import { twMerge, twJoin } from "tailwind-merge";
import SupLock from "../../../icons/Lock";

import { SupText } from "../Text";
import { type CommonColors } from "../../../types/colors";

interface Field {
  id?: string;
  color?: CommonColors;
  fluid?: boolean;
  disabled?: boolean;
}

export interface FieldControlProps {
  label?: string | JSX.Element;
  id: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  hint?: string | JSX.Element;
  hintAlign?: "left" | "right";
  color?: CommonColors;
  icon?: JSX.Element;
  fluid?: boolean;
  prepend?: string | JSX.Element;
  children: ReactElement<Field>;
  readOnly?: boolean;
}

const styles = {
  primary: "text-neutral-950",
  success: "text-success-800",
  error: "text-error-800",
  warning: "text-warning-800",
  info: "text-info-800",
};

export const borderStyles = {
  primary: "border-neutral-500",
  success: "border-success-900",
  info: "border-info-800",
  error: "border-error-800",
  warning: "border-warning-800",
} as const;

export function SupFieldControl({
  className,
  label,
  id,
  icon,
  required,
  readOnly = false,
  hint,
  hintAlign = "left",
  disabled = false,
  fluid = false,
  color = "primary",
  prepend,
  children,
}: FieldControlProps) {
  return (
    <div className={twMerge("sup-field-control inline-flex flex-col max-w-full", fluid && "w-full", !disabled || readOnly ? styles[color] : "text-neutral-500", className)}>
      {(Boolean(label) || Boolean(icon)) && (
        <div className="flex items-center h-5 mb-1">
          {Boolean(label) && readOnly && <SupLock className="text-neutral-600 w-4 h-4 mx-1" />}

          {Boolean(label) && (
            <SupText as="label" variant="caption" htmlFor={id}>
              {label}
              {required && <span className="text-error-700">*</span>}
            </SupText>
          )}

          {Boolean(icon) && <span className={twJoin("ml-auto ", !disabled || readOnly ? "text-neutral-600" : "text-neutral-100")}>{icon}</span>}
        </div>
      )}

      <div className="flex items-stretch">
        {Boolean(prepend) && (
          <SupText
            as="div"
            variant="caption"
            className={twJoin("flex items-center shrink-0 max-w-[12.25rem] bg-neutral-50 py-1 px-2 border-0.5 border-r-0", !disabled || readOnly ? borderStyles[color] : "border-neutral-100")}
          >
            {typeof prepend === "string" ? (
              <SupText as="span" variant="caption" className="overflow-hidden whitespace-nowrap text-ellipsis text-neutral-700">
                {prepend}
              </SupText>
            ) : (
              prepend
            )}
          </SupText>
        )}

        {cloneElement(children, { id, color, fluid, disabled })}
      </div>

      {Boolean(hint) && (
        <SupText as="div" variant="caption" className={twJoin("mt-1", hintAlign === "left" ? "text-left" : "text-right")}>
          {hint}
        </SupText>
      )}
    </div>
  );
}
